import React, { useState, useEffect } from 'react';
import { Button } from '@/components';
import { useUrlTab } from '@/hooks/useUrlListState';
import { Handover } from '@/types/common';
// The complete rung list lives here; the one in @/types/common predates Dash
// Rent and has no WAITING_RETURN.
import { HandoverStatus } from '@/types/rental';
import {
  createHandover,
  updateHandover,
  CreateHandoverRequest,
  UpdateHandoverRequest,
  downloadActiveHandoversCsv,
  downloadReturnedHandoversCsv,
  getHandoverStats,
  HandoverStats,
} from '@/services/api/handovers';
import { vehiclesApi } from '@/services/api/vehicles';
import { getListProvider } from '@/services/api/providers';
import { getListPitstop } from '@/services/api/pitstops';
import { getListOem } from '@/services/api/oems';
import { getListModel } from '@/services/api/models';
import {
  AddHandoverModal,
  ActiveHandoverTab,
  EditScheduleModal,
  HistoryHandoverTab,
  PreReturnHandoverTab,
} from './components';
import {
  showSuccessToast,
  showErrorToast,
} from '@/components/toast/globalToast';
import { extractApiErrorMessage, getUserEmail } from '@/utils';
import { AddHandoverModalInitialData } from './components/AddHandoverModal';

// Left to right in the order a unit moves: reserved at the hub, out with the
// driver, return scheduled, back. `statKey` names this tab's bucket in the stats
// response, so a badge cannot be wired to the wrong count.
const TABS = [
  {
    key: 'waiting_handover' as const,
    label: 'Waiting Handover',
    statKey: 'waitingHandover' as const,
  },
  {
    key: 'active' as const,
    label: 'Active Handover',
    statKey: 'active' as const,
  },
  {
    key: 'waiting_return' as const,
    label: 'Waiting Return',
    statKey: 'waitingReturn' as const,
  },
  {
    key: 'history' as const,
    label: 'Returned Handover',
    statKey: 'returned' as const,
  },
];

const HANDOVER_TABS = [
  'waiting_handover',
  'active',
  'waiting_return',
  'pre_return',
  'history',
] as const;

const HandoverListPage: React.FC = () => {
  // In the URL with the tab's own filters, so a refresh lands on the same list.
  const [activeTab, setActiveTab] = useUrlTab(HANDOVER_TABS, 'active');

  // Every badge, from one endpoint. These used to be per-tab totals reported by
  // whichever tab was mounted, so a badge read zero until its tab was clicked.
  const [stats, setStats] = useState<HandoverStats | null>(null);
  const [, setPreReturnHandoverTotal] = useState(0);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingHandover, setEditingHandover] = useState<Handover | null>(null);
  const [prefilledHandoverData, setPrefilledHandoverData] =
    useState<AddHandoverModalInitialData | null>(null);

  // The row whose schedule is being overridden. Owned here rather than in a tab
  // because the same modal is opened from two places — the row's action menu and
  // the detail popup — and a refresh has to reach the list either way.
  const [scheduleTarget, setScheduleTarget] = useState<Handover | null>(null);

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [activeDownloadParams, setActiveDownloadParams] = useState<
    Record<string, any>
  >({});
  const [waitingHandoverDownloadParams, setWaitingHandoverDownloadParams] =
    useState<Record<string, any>>({});
  const [waitingReturnDownloadParams, setWaitingReturnDownloadParams] =
    useState<Record<string, any>>({});
  const [preReturnDownloadParams, setPreReturnDownloadParams] = useState<
    Record<string, any>
  >({});
  const [historyDownloadParams, setHistoryDownloadParams] = useState<
    Record<string, any>
  >({});

  const [vehicles, setVehicles] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [pitstops, setPitstops] = useState<any[]>([]);
  const [hubLocations, setHubLocations] = useState<any[]>([]);
  const [oems, setOems] = useState<any[]>([]);
  const [models, setModels] = useState<any[]>([]);

  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const vehiclesResponse = await vehiclesApi.getVehicles(1, 1000);
        setVehicles(vehiclesResponse.data || []);

        // Load clients and pitstops from API
        const clientsResponse = await getListProvider({
          page: '1',
          size: '9999',
        });
        setClients(clientsResponse.data || []);

        const pitstopsResponse = await getListPitstop({
          page: '1',
          size: '9999',
          type: 'PROVIDER_WAREHOUSE',
        });
        setPitstops(pitstopsResponse.data || []);

        const hubsResponse = await getListPitstop({
          page: '1',
          size: '9999',
          type: 'HUB',
          hasStaff: true,
        });
        setHubLocations(hubsResponse.data || []);

        // Load OEMs and Models from API
        const oemsResponse = await getListOem({
          page: '1',
          size: '9999',
        });
        setOems(oemsResponse.data || []);

        const modelsResponse = await getListModel({
          page: '1',
          size: '9999',
        });
        setModels(modelsResponse.data || []);
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to load filter data'
        );
        showErrorToast('Error', errorMessage);
      }
    };
    loadFilterData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadStats = async () => {
      try {
        const result = await getHandoverStats();
        if (!cancelled) setStats(result);
      } catch {
        // A failed badge must not blank the list it labels — the tabs still work
        // without their counts, so this stays silent rather than toasting.
        if (!cancelled) setStats(null);
      }
    };
    loadStats();
    return () => {
      cancelled = true;
    };
  }, [refreshTrigger]);

  const handleEditHandover = (handover: Handover) => {
    setPrefilledHandoverData(null);
    setEditingHandover(handover);
    setIsAddModalOpen(true);
  };

  const handleSaveHandover = async (
    data: CreateHandoverRequest | UpdateHandoverRequest
  ) => {
    setIsCreating(true);
    try {
      const userEmail = getUserEmail();

      if (editingHandover) {
        const updatePayload: UpdateHandoverRequest = {
          ...data,
          updatedBy: userEmail,
        };

        await updateHandover(editingHandover.ID, updatePayload);
        showSuccessToast('Success', 'Handover updated successfully');
      } else {
        const createPayload: CreateHandoverRequest = {
          ...(data as CreateHandoverRequest),
          createdBy: userEmail,
        };

        await createHandover(createPayload);
        showSuccessToast('Success', 'Handover created successfully');
      }

      setIsAddModalOpen(false);
      setEditingHandover(null);
      setPrefilledHandoverData(null);
      // Trigger refresh in both tabs
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      const fallbackMessage = `Failed to ${
        editingHandover ? 'update' : 'create'
      } handover`;
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      showErrorToast('Error', errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleTabChange = (
    tab:
      | 'waiting_handover'
      | 'active'
      | 'waiting_return'
      | 'pre_return'
      | 'history'
  ) => {
    setActiveTab(tab);
  };

  const handleDownloadCsv = async () => {
    setIsDownloading(true);
    try {
      // Only the returned tab reads the history endpoint; the rest share the
      // active one, which takes the rung as a param. Each tab sends its own
      // params so a filter set on one cannot leak into another's download.
      const perTab = {
        waiting_handover: {
          params: waitingHandoverDownloadParams,
          prefix: 'handovers-waiting-handover',
        },
        active: {
          params: activeDownloadParams,
          prefix: 'handovers-active',
        },
        waiting_return: {
          params: waitingReturnDownloadParams,
          prefix: 'handovers-waiting-return',
        },
        pre_return: {
          params: preReturnDownloadParams,
          prefix: 'handovers-pre-return',
        },
        history: {
          params: historyDownloadParams,
          prefix: 'handovers-history',
          fromHistory: true,
        },
      } as const;

      const tab = perTab[activeTab];
      const params = tab.params;
      const blob =
        'fromHistory' in tab
          ? await downloadReturnedHandoversCsv(params)
          : await downloadActiveHandoversCsv(params);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateStamp = new Date().toISOString().slice(0, 10);
      const filePrefix = tab.prefix;

      link.href = url;
      link.download = `${filePrefix}-${dateStamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccessToast('Success', 'Download started');
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to download handovers'
      );
      showErrorToast('Error', errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
              Handover
            </h1>
            <p className="text-[14px] text-gray-600">
              Track all vehicle handover events.
            </p>
          </div>
          <div className="flex gap-2">
            <>
              <Button
                variant="white"
                size="small"
                onClick={handleDownloadCsv}
                disabled={isDownloading}
              >
                <i className="ri-download-line text-[12px] mr-1"></i>
                {isDownloading ? 'Downloading...' : 'Download'}
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={() => {
                  setEditingHandover(null);
                  setPrefilledHandoverData(null);
                  setIsAddModalOpen(true);
                }}
              >
                <i className="ri-add-line text-[12px] mr-1"></i>
                Create Handover
              </Button>
            </>
          </div>
        </div>
      </div>

      {/* Tabs. Ordered by the rung a unit physically moves through, and driven by
          one stats call so every badge is populated before its tab is opened. */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {TABS.map(({ key, label, statKey }) => {
              const isActive = activeTab === key;
              const count = stats?.[statKey] ?? 0;
              return (
                <button
                  key={key}
                  onClick={() => handleTabChange(key)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    isActive
                      ? 'border-[#5e2aac] text-[#5e2aac]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span>{label}</span>
                  {count > 0 && (
                    <span
                      className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full ${
                        isActive
                          ? 'bg-[#5e2aac] text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {/* waiting_handover, active and waiting_return share ActiveHandoverTab;
          `key={activeTab}` remounts it per tab so its search box and table
          state start clean along with the cleared query. */}
      {activeTab === 'waiting_handover' ? (
        // Reserved and paid, still at the hub. No Return action — unitnya
        // belum pernah keluar, jadi mengembalikannya adalah pembatalan dan
        // backend menolaknya. Date range-nya menyaring pickup yang
        // DIRENCANAKAN, karena baris ini belum punya handoverDate.
        <ActiveHandoverTab
          key={activeTab}
          statuses={HandoverStatus.WAITING_HANDOVER}
          storageKey="handovers-waiting-handover"
          canReturn={false}
          scheduleLeg="handover"
          onEditSchedule={setScheduleTarget}
          onEditHandover={handleEditHandover}
          refreshTrigger={refreshTrigger}
          vehicles={vehicles}
          clients={clients}
          pitstops={pitstops}
          hubLocations={hubLocations}
          oems={oems}
          models={models}
          onDownloadParamsChange={setWaitingHandoverDownloadParams}
        />
      ) : activeTab === 'active' ? (
        <ActiveHandoverTab
          key={activeTab}
          onEditHandover={handleEditHandover}
          refreshTrigger={refreshTrigger}
          vehicles={vehicles}
          clients={clients}
          pitstops={pitstops}
          hubLocations={hubLocations}
          oems={oems}
          models={models}
          onDownloadParamsChange={setActiveDownloadParams}
        />
      ) : activeTab === 'waiting_return' ? (
        // A return is scheduled but the unit is still with the driver. Same row
        // shape as Active, so the same tab renders it — only the rung differs.
        // Return tetap tersedia: possession-nya belum pindah, jadi unit yang
        // pulang sesuai jadwal dicatat lewat tombol yang sama dengan Active.
        <ActiveHandoverTab
          key={activeTab}
          statuses={HandoverStatus.WAITING_RETURN}
          storageKey="handovers-waiting-return"
          scheduleLeg="return"
          onEditSchedule={setScheduleTarget}
          onEditHandover={handleEditHandover}
          refreshTrigger={refreshTrigger}
          vehicles={vehicles}
          clients={clients}
          pitstops={pitstops}
          hubLocations={hubLocations}
          oems={oems}
          models={models}
          onDownloadParamsChange={setWaitingReturnDownloadParams}
        />
      ) : activeTab === 'pre_return' ? (
        <PreReturnHandoverTab
          onEditHandover={handleEditHandover}
          refreshTrigger={refreshTrigger}
          vehicles={vehicles}
          clients={clients}
          pitstops={pitstops}
          hubLocations={hubLocations}
          oems={oems}
          models={models}
          onTotalChange={setPreReturnHandoverTotal}
          onDownloadParamsChange={setPreReturnDownloadParams}
        />
      ) : (
        <HistoryHandoverTab
          onEditHandover={handleEditHandover}
          refreshTrigger={refreshTrigger}
          vehicles={vehicles}
          clients={clients}
          pitstops={pitstops}
          hubLocations={hubLocations}
          oems={oems}
          models={models}
          onDownloadParamsChange={setHistoryDownloadParams}
        />
      )}

      {/* Add Handover Modal */}
      <AddHandoverModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingHandover(null);
          setPrefilledHandoverData(null);
        }}
        onSubmit={handleSaveHandover}
        isLoading={isCreating}
        initialData={editingHandover || prefilledHandoverData}
        isEditMode={!!editingHandover}
        // Detail view only, and only on a waiting rung — the detail view decides,
        // since it is the one holding the record's status.
        onEditSchedule={
          editingHandover ? () => setScheduleTarget(editingHandover) : undefined
        }
      />

      {/* Ops date override, for the rung that has a date pending. */}
      <EditScheduleModal
        isOpen={scheduleTarget !== null}
        handover={scheduleTarget}
        onClose={() => setScheduleTarget(null)}
        onSuccess={() => {
          // The popup behind this one is showing the old date, so close it too
          // rather than leave two answers on screen.
          setIsAddModalOpen(false);
          setEditingHandover(null);
          setRefreshTrigger((prev) => prev + 1);
        }}
      />
    </div>
  );
};

export default HandoverListPage;
